import { Check } from 'lucide-react'
import styles from '../../styles/emailTemplateCard.module.css'
import { cn } from '@/lib/utils/helpers'
import type { EmailTemplateStatus } from '../../types/emailTemplate'
import type { EmailTemplateAddEditFormProps } from '../../types/components'
import { EmailTemplateFieldError } from './EmailTemplateFieldError'
import { EmailTemplateToggleButton } from './EmailTemplateToggleButton'

export function EmailTemplateStatusSection({ page }: EmailTemplateAddEditFormProps) {
  const {
    isEdit,
    templateDetail,
    passthruMailgun,
    register,
    watch,
    showPassthru,
    showFieldError,
    fieldErrorMessage,
    setValue,
    clearErrors,
    validateFieldOnBlur,
    openSendTestMail,
  } = page

  const templateStatus = watch('template_status')
  const isActive = templateStatus !== 'DISABLED'

  const setTemplateStatus = (value: EmailTemplateStatus) => {
    if (String(templateStatus) === value) return
    setValue('template_status', value, { shouldDirty: true, shouldTouch: true })
    clearErrors('template_status')
    validateFieldOnBlur('template_status')
  }

  const setPassthru = (value: '0' | '1') => {
    setValue('passthru_mailgun', value, { shouldDirty: true, shouldTouch: true })
    clearErrors('passthru_mailgun')
    validateFieldOnBlur('passthru_mailgun')
  }

  return (
    <div
      data-testid="et-section-status"
      className={styles.card}
    >
      <div className={styles.cardHeader}>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center">
            <h3 className={styles.cardTitle}>Email Status</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openSendTestMail('Email')}
              className="inline-flex w-full min-w-0 items-center justify-center rounded-lg bg-[#1597ff] px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0d7acc] sm:w-auto sm:shrink-0"
              data-testid="email-template-send-test-mail"
            >
              Send test mail
            </button>
            {isEdit && templateDetail?.is_sms === '1' && (
              <button
                type="button"
                onClick={() => openSendTestMail('SMS')}
                className="inline-flex w-full min-w-0 items-center justify-center rounded-lg bg-[#1597ff] px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0d7acc] sm:w-auto sm:shrink-0"
                data-testid="email-template-send-test-sms"
              >
                Send test SMS
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={cn(styles.cardBody, 'space-y-4')}>
        <div>
          <p className="mb-2 text-sm text-slate-600">Please select status of email template</p>
          <div
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200/80 bg-slate-100 p-1 text-xs font-medium"
            role="group"
            aria-label="Template active or disabled"
            data-testid="et-status-toggle-group"
          >
            <button
              type="button"
              onClick={() => setTemplateStatus('ACTIVE')}
              data-testid="et-status-active-toggle"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all duration-150',
                isActive
                  ? 'border border-[#0c80dc]/30 bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] font-semibold text-white shadow-sm shadow-sky-500/20'
                  : 'border border-slate-200/80 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              {isActive && <Check className="h-3.5 w-3.5" />}
              <span>Active</span>
            </button>
            <button
              type="button"
              onClick={() => setTemplateStatus('DISABLED')}
              data-testid="et-status-disabled-toggle"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all duration-150',
                !isActive
                  ? 'border border-[#0c80dc]/30 bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] font-semibold text-white shadow-sm shadow-sky-500/20'
                  : 'border border-slate-200/80 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              {!isActive && <Check className="h-3.5 w-3.5" />}
              <span>Disabled</span>
            </button>
          </div>
        </div>

        <EmailTemplateFieldError
          show={showFieldError('template_status')}
          message={fieldErrorMessage('template_status')}
          className="mb-2"
        />

        <input type="hidden" {...register('template_status')} />
        <input type="hidden" {...register('passthru_mailgun')} />

        {showPassthru && (
          <div className="mt-3 w-full min-w-0 border-t border-slate-200 py-3">
            <p className={cn(styles.formLabel, '!mb-0 !pb-1')} id="et-passthru-label">
              Passthru Mailgun
            </p>
            <div
              className="mt-2 flex flex-wrap items-center gap-2.5"
              role="group"
              aria-labelledby="et-passthru-label"
            >
              <EmailTemplateToggleButton
                selected={String(passthruMailgun ?? '') === '1'}
                onClick={() => setPassthru('1')}
                testId="et-passthru-yes"
              >
                Yes
              </EmailTemplateToggleButton>
              <EmailTemplateToggleButton
                selected={String(passthruMailgun ?? '') === '0'}
                onClick={() => setPassthru('0')}
                testId="et-passthru-no"
              >
                No
              </EmailTemplateToggleButton>
            </div>
            <EmailTemplateFieldError
              show={showFieldError('passthru_mailgun')}
              message={fieldErrorMessage('passthru_mailgun')}
              className="mt-2"
            />
          </div>
        )}
      </div>
    </div>
  )
}
